import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, mergeMap } from 'rxjs/operators';
import { ProjectHumanResourceService } from '@/core/services/project-human-resource.service';
import {
  loadProjectHumanResourcesByProject,
  loadProjectHumanResourcesByProjectSuccess,
  loadProjectHumanResourcesByProjectFailure,
  createProjectHumanResources,
  createProjectHumanResourcesSuccess,
  createProjectHumanResourcesFailure,
  updateProjectHumanResource,
  updateProjectHumanResourceSuccess,
  updateProjectHumanResourceFailure,
  updateProjectHumanResourcesRange,
  updateProjectHumanResourcesRangeSuccess,
  updateProjectHumanResourcesRangeFailure,
  deleteProjectHumanResource,
  deleteProjectHumanResourceSuccess,
  deleteProjectHumanResourceFailure,
} from './project-human-resource.actions';

@Injectable()
export class ProjectHumanResourceEffects {
  private readonly actions$ = inject(Actions);
  private readonly service = inject(ProjectHumanResourceService);

  loadByProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProjectHumanResourcesByProject),
      switchMap(({ projectId }) =>
        this.service.getByProjectId(projectId).pipe(
          map(items => loadProjectHumanResourcesByProjectSuccess({ items, projectId })),
          catchError(error => of(loadProjectHumanResourcesByProjectFailure({ error })))
        )
      )
    )
  );

  createRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createProjectHumanResources),
      mergeMap(({ items }) =>
        this.service.createRange(items).pipe(
          map(created => createProjectHumanResourcesSuccess({ items: created })),
          catchError(error => of(createProjectHumanResourcesFailure({ error })))
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProjectHumanResource),
      mergeMap(({ item }) =>
        this.service.update(item).pipe(
          map(updated => updateProjectHumanResourceSuccess({ item: updated })),
          catchError(error => of(updateProjectHumanResourceFailure({ error })))
        )
      )
    )
  );

  updateRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateProjectHumanResourcesRange),
      mergeMap(({ items }) =>
        this.service.updateRange(items).pipe(
          map(() => updateProjectHumanResourcesRangeSuccess({ items })),
          catchError(error => of(updateProjectHumanResourcesRangeFailure({ error })))
        )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteProjectHumanResource),
      mergeMap(({ id }) =>
        this.service.delete(id).pipe(
          map(() => deleteProjectHumanResourceSuccess({ id })),
          catchError(error => of(deleteProjectHumanResourceFailure({ error })))
        )
      )
    )
  );
}
